// MARK://dhk8/admin-shell/pages/modules/PurchaseOrdersPage
// Lista de órdenes de compra → dhk8-erp-purchase-orders.
// Lista DS: DataTable + FilterBar (búsqueda por folio/proveedor + filtro de
// estado) + SummaryStrip (borradores / recibidas / total comprado). Estado de la
// OC como StatusPill por color. Conserva curvas/tokens; presentación + wiring.
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Inbox, FileText } from 'lucide-react'
import * as po from '../../lib/clients/purchase-orders.js'
import * as sup from '../../lib/clients/suppliers.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill from '../../components/StatusPill.js'

// Estado de la OC → tono de StatusPill (Borrador=info, Enviada/Parcial=warn,
// Aprobada/Recibida=ok, Cancelada=danger).
const PO_STATUS_TONE: Record<po.POStatus, 'ok' | 'warn' | 'danger' | 'info' | 'neutral'> = {
  DRAFT: 'info',
  SUBMITTED: 'warn',
  APPROVED: 'ok',
  PARTIAL: 'warn',
  RECEIVED: 'ok',
  CANCELLED: 'danger',
}

type StatusFilter = 'all' | po.POStatus

export default function PurchaseOrdersPage() {
  const navigate = useNavigate()
  const list = useAsync((s) => po.listPurchaseOrders(s))
  const suppliers = useAsync((s) => sup.listSuppliers(s))

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [sort, setSort] = useState<SortState | null>({ key: 'createdAt', dir: 'desc' })

  const supplierName = (sid: string): string =>
    suppliers.data?.find((x) => x.id === sid)?.legalName ?? '(proveedor no encontrado)'

  const rows = useMemo(() => {
    const all = list.data ?? []
    const q = search.trim().toLowerCase()
    let out = all.filter((o) => {
      if (status !== 'all' && o.status !== status) return false
      if (!q) return true
      return o.id.toLowerCase().includes(q) || supplierName(o.supplierId).toLowerCase().includes(q)
    })
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      out = [...out].sort((a, b) => {
        if (sort.key === 'total') return (a.totalCents - b.totalCents) * dir
        if (sort.key === 'supplier') return supplierName(a.supplierId).localeCompare(supplierName(b.supplierId), 'es-MX') * dir
        // createdAt (default)
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
      })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, suppliers.data, search, status, sort])

  const all = list.data ?? []
  const drafts = all.filter((o) => o.status === 'DRAFT').length
  const received = all.filter((o) => o.status === 'RECEIVED').length
  const totalCents = all.reduce((s, o) => s + o.totalCents, 0)

  const columns: DataTableColumn<po.PurchaseOrder>[] = [
    {
      key: 'folio', header: 'Folio', sortable: false,
      render: (o) => (
        <span>
          <FileText size={14} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6, opacity: 0.6 }} />
          <span className="adm-mono">{o.id.slice(0, 8).toUpperCase()}</span>
        </span>
      ),
    },
    { key: 'supplier', header: 'Proveedor', sortable: true, render: (o) => supplierName(o.supplierId) },
    { key: 'lineas', header: 'Líneas', align: 'right', render: (o) => <span className="adm-mono">{o.lines.length}</span> },
    { key: 'createdAt', header: 'Creada', sortable: true, render: (o) => new Date(o.createdAt).toLocaleDateString('es-MX') },
    { key: 'total', header: 'Total', align: 'right', sortable: true, render: (o) => <span className="adm-mono">{mxn(o.totalCents)}</span> },
    { key: 'estado', header: 'Estado', render: (o) => <StatusPill tone={PO_STATUS_TONE[o.status]} label={po.PO_STATUS_LABEL[o.status]} /> },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Órdenes de compra</h1><p className="adm-page__sub">Pedidos a proveedores y su estado</p></div>
        <div className="adm-page__actions">
          <Link className="adm-btn" to="/purchase-orders/inbox"><Inbox size={16} /> Bandeja del proveedor</Link>
        </div>
      </div>

      {(all.length > 0 || list.loading) && (
        <div style={{ marginBottom: 'var(--pos-space-3, 12px)' }}>
          <SummaryStrip items={[
            { label: 'Borradores', value: drafts, tone: drafts > 0 ? 'info' : 'neutral' },
            { label: 'Recibidas', value: received, tone: 'ok' },
            { label: 'Total comprado', value: mxn(totalCents) },
          ]} />
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable
          columns={columns}
          rows={rows}
          getRowKey={(o) => o.id}
          zebra
          sort={sort}
          onSortChange={setSort}
          onRowClick={(o) => navigate(`/purchase-orders/${o.id}`)}
          emptyText={search || status !== 'all' ? 'Sin órdenes que coincidan con el filtro.' : 'Sin órdenes de compra. Crea la primera para reabastecer.'}
          toolbar={
            <FilterBar
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Buscar por folio o proveedor…"
              filters={[{
                key: 'status', label: 'Estado', value: status,
                onChange: (v) => setStatus(v as StatusFilter),
                options: [
                  { value: 'all', label: 'Todos' },
                  { value: 'DRAFT', label: 'Borrador' },
                  { value: 'SUBMITTED', label: 'Enviada' },
                  { value: 'APPROVED', label: 'Aprobada' },
                  { value: 'PARTIAL', label: 'Parcial' },
                  { value: 'RECEIVED', label: 'Recibida' },
                  { value: 'CANCELLED', label: 'Cancelada' },
                ],
              }]}
              actions={<Link className="adm-btn adm-btn--primary adm-btn--sm" to="/purchase-orders/new"><Plus size={16} /> Nueva orden</Link>}
            />
          }
        />
      )}
    </div>
  )
}
